/**
 * Component Tree Transformer
 *
 * Transforms Figma's internal scene graph format to a web-friendly component tree format.
 * Expands INSTANCE nodes, resolves colors, and generates semantic hints.
 */

import { ClipboardSceneParser } from './parser.js'
import { parseClipboardScene } from './index.js'
import type { FigmaInternalScene, SceneNode } from './types/scene.js'
import type { InstanceProps, SymbolOverride } from './types/properties/instance.js'
import type { TextProps } from './types/properties/text.js'
import type {
  ComponentNode,
  ComponentNodeType,
  ComponentTree,
  ContentProperties,
  MetadataProperties,
} from './types/component-tree.js'
import { extractLayout } from './utils/layout-mapper.js'
import { extractStyle } from './utils/style-mapper.js'
import { resolveSingleColor } from './utils/color-resolver.js'
import {
  detectComponentRole,
  detectIconType,
  isStructuralFrame,
  suggestHtmlTag,
} from './utils/semantic-analyzer.js'
import {
  buildStyleLookupMap,
  buildVariableLookupMap,
  collectNodeHints,
  type AssetLookupMap,
} from './utils/hint-resolver.js'
import {
  applyComponentPropAssignments,
  buildPropDefLookup,
} from './utils/component-prop-resolver.js'

/**
 * Options for component tree transformation
 */
export interface TransformOptions {
  /**
   * Whether to expand INSTANCE nodes to their symbol definitions
   * @default true
   */
  expandInstances?: boolean

  /**
   * Whether to filter out hidden nodes
   * @default true
   */
  filterHidden?: boolean

  /**
   * Whether to detect and mark structural/organizational frames
   * @default true
   */
  detectStructuralFrames?: boolean

  /**
   * Whether to detect and mark icon components in metadata
   * @default true
   */
  detectIcons?: boolean

  /**
   * Whether to sort children by y-position (visual order)
   * When true, children are sorted top-to-bottom based on their y coordinate
   * @default true
   */
  sortChildrenByPosition?: boolean

  /**
   * Whether to include position coordinates for auto-layout children
   * Position coords are Figma-internal and don't translate to CSS flexbox
   * @default false
   */
  includePositionCoords?: boolean

  /**
   * Whether to flatten structural single-child wrappers
   * Reduces unnecessary nesting from Figma's organizational frames
   * @default true
   */
  flattenStructuralWrappers?: boolean

  /**
   * Whether to use flexible dimensions for containers
   * When true, containers use 'auto' or '100%' instead of exact pixel widths
   * @default true
   */
  useFlexibleDimensions?: boolean

  /**
   * Whether to apply instance overrides when expanding instances
   * Only takes effect when expandInstances is true
   * When enabled, text overrides, visibility overrides, and other customizations
   * are applied to the expanded component tree
   * @default false
   */
  applyInstanceOverrides?: boolean

  /**
   * Whether to apply component property assignments when expanding instances
   * Only takes effect when expandInstances is true
   * When enabled, component properties (TEXT, BOOLEAN) are resolved and applied
   * @default true
   */
  applyComponentProps?: boolean

  /**
   * Whether to include node.style section in component tree output
   * Controls: background, border, radius, shadow, blur, opacity
   * @default true
   */
  includeNodeStyle?: boolean

  /**
   * Whether to include content.text.style section in component tree output
   * Controls: fontFamily, fontSize, fontWeight, color
   * @default true
   */
  includeTextStyle?: boolean

  /**
   * Whether to include layout dimensional values in component tree output
   * Controls: gap, padding, width, height
   * Structural properties (mode, align, sizing) always included
   * @default true
   */
  includeLayoutDimensions?: boolean

  /**
   * Whether to include metadata in component tree output
   * Controls: ComponentTree.version, ComponentTree.source, ComponentNode.id, ComponentNode.meta
   * When false, produces minimal output focused on structure and styling only
   * @default true
   */
  includeMetadata?: boolean
}

/**
 * Component Tree Transformer
 *
 * Transforms clipboard scene data into a structured component tree
 * optimized for Claude Code to implement UI components.
 */
export class ComponentTreeTransformer {
  private parser: ClipboardSceneParser
  private options: Required<TransformOptions>
  private assetLookupMap: AssetLookupMap

  constructor() {
    this.parser = new ClipboardSceneParser()
    // Set default options
    this.options = {
      expandInstances: true,
      filterHidden: true,
      detectStructuralFrames: true,
      detectIcons: true,
      sortChildrenByPosition: true,
      includePositionCoords: false,
      flattenStructuralWrappers: true,
      useFlexibleDimensions: true,
      applyInstanceOverrides: true,
      applyComponentProps: true,
      includeNodeStyle: true,
      includeTextStyle: true,
      includeLayoutDimensions: true,
      includeMetadata: false,
    }
    this.assetLookupMap = new Map()
  }

  /**
   * Transform clipboard data to component tree format
   *
   * @param clipboardData - Raw clipboard data (JSON object or string)
   * @param options - Transformation options
   * @returns Transformed component tree
   */
  transform(clipboardData: unknown, options?: TransformOptions): ComponentTree {
    // Merge options with defaults
    this.options = {
      expandInstances: options?.expandInstances ?? true,
      filterHidden: options?.filterHidden ?? true,
      detectStructuralFrames: options?.detectStructuralFrames ?? true,
      detectIcons: options?.detectIcons ?? true,
      sortChildrenByPosition: options?.sortChildrenByPosition ?? true,
      includePositionCoords: options?.includePositionCoords ?? false,
      flattenStructuralWrappers: options?.flattenStructuralWrappers ?? true,
      useFlexibleDimensions: options?.useFlexibleDimensions ?? true,
      applyInstanceOverrides: options?.applyInstanceOverrides ?? true,
      applyComponentProps: options?.applyComponentProps ?? true,
      includeNodeStyle: options?.includeNodeStyle ?? true,
      includeTextStyle: options?.includeTextStyle ?? true,
      includeLayoutDimensions: options?.includeLayoutDimensions ?? true,
      includeMetadata: options?.includeMetadata ?? false,
    }
    const opts = this.options

    // 1. Parse clipboard using existing parser
    const scene = parseClipboardScene(clipboardData)
    const component = this.parser.parseFirstChild(scene, opts.filterHidden)

    if (!component) {
      throw new Error('No visible component found in clipboard')
    }

    // 2. Parse variable registry BEFORE expansion
    const variableRegistry = this.parser.parseVariableRegistry(scene)

    // 3. Expand INSTANCE nodes recursively (if enabled)
    const expanded = opts.expandInstances ? this.expandInstances(component, scene) : component

    // 4. Filter hidden nodes AFTER expansion (if enabled)
    // This is necessary because symbol definitions may contain hidden nodes
    const filtered = opts.filterHidden ? this.filterHiddenNodes(expanded) : expanded

    if (!filtered) {
      throw new Error('Component became null after filtering hidden nodes')
    }

    // 5. Build variable and style lookup maps, combine them for per-node collection
    const variableLookupMap = buildVariableLookupMap(variableRegistry)
    const styleLookupMap = buildStyleLookupMap(variableRegistry)

    // Combine both maps for unified resolution (styles take precedence if keys overlap)
    this.assetLookupMap = new Map<string, any>([...variableLookupMap, ...styleLookupMap])

    // 6. Transform to component tree format (mark as root for sizing intent detection)
    const root = this.transformNode(filtered, undefined, true)

    // 7. Build complete tree (conditionally include metadata)
    // Always include fileKey for image downloading and file linking
    const tree: ComponentTree = this.options.includeMetadata
      ? {
          version: '1.0',
          source: {
            type: 'clipboard',
            fileName: component.name,
            timestamp: new Date().toISOString(),
            fileKey: scene.metadata.fileKey,
          },
          root,
        }
      : {
          source: {
            type: 'clipboard',
            fileKey: scene.metadata.fileKey,
          },
          root,
        }

    return tree
  }

  /**
   * Recursively expand INSTANCE nodes by replacing with symbol definition
   *
   * @param node - Node to expand
   * @param scene - Full scene data for symbol lookup
   * @returns Expanded node
   */
  private expandInstances(node: SceneNode, scene: FigmaInternalScene): SceneNode {
    if (node.type === 'INSTANCE') {
      // Get symbol definition
      const instanceProps = node.properties as InstanceProps
      const symbolId = instanceProps.symbolData?.symbolID

      if (symbolId) {
        const symbolGuid = `${symbolId.sessionID}:${symbolId.localID}`

        // Find symbol definition in canvas (Internal Only Canvas)
        const canvas = this.getInternalOnlyCanvas(scene)
        if (canvas) {
          const symbolNode = this.parser.findNodeByGuid(canvas, symbolGuid)

          if (symbolNode) {
            // Clone symbol node to avoid modifying original
            const expanded = this.cloneNode(symbolNode)

            // FIX: Preserve the instance's GUID, not the symbol's
            // This ensures each instance has a unique ID even if they share the same symbol
            expanded.guid = node.guid

            // FIX: Preserve the instance's transform/position and visibility
            // This ensures instances are positioned correctly relative to their parent,
            // not at the symbol definition's absolute canvas position
            // Also preserves visibility so hidden instances stay hidden
            const nodeProps = node.properties as any
            const expandedProps = expanded.properties as any

            const overrides: any = {}
            if (nodeProps.transform) {
              overrides.transform = nodeProps.transform
            }
            // CRITICAL: Preserve instance visibility
            if (nodeProps.visible !== undefined) {
              overrides.visible = nodeProps.visible
            }

            // Store instance expansion metadata
            overrides.__instanceMetadata = {
              componentId: symbolGuid,
              componentName: symbolNode.name,
              symbolOverrides: instanceProps.symbolData.symbolOverrides,
            }

            if (Object.keys(overrides).length > 0) {
              ;(expanded.properties as any) = {
                ...expandedProps,
                ...overrides,
              }
            }

            // Apply instance overrides if enabled
            if (this.options.applyInstanceOverrides && instanceProps.symbolData.symbolOverrides) {
              this.applySymbolOverrides(expanded, instanceProps.symbolData.symbolOverrides)
            }

            // Apply component property assignments if enabled
            if (this.options.applyComponentProps && instanceProps.componentPropAssignments) {
              const propDefLookup = buildPropDefLookup(symbolNode)
              applyComponentPropAssignments(
                expanded,
                instanceProps.componentPropAssignments,
                propDefLookup
              )
            }

            // Recursively expand nested instances
            if (expanded.children) {
              expanded.children = expanded.children.map((child) =>
                this.expandInstances(child, scene),
              )
            }

            return expanded
          } else {
            // Symbol definition not found - return placeholder frame
            console.warn(`Symbol definition not found for instance ${node.guid}: ${symbolGuid}`)
            return {
              ...node,
              type: 'FRAME',
              name: `${node.name} (Missing Symbol)`,
              children: [],
            }
          }
        }
      }
    }

    // For non-instance nodes, recursively expand children
    if (node.children) {
      const expandedChildren = node.children.map((child) => this.expandInstances(child, scene))
      return {
        ...node,
        children: expandedChildren,
      }
    }

    return node
  }

  /**
   * Apply symbol overrides to an expanded component tree
   *
   * Symbol overrides allow instances to customize text content, visibility,
   * and other properties without modifying the component definition.
   *
   * @param node - The expanded node tree to apply overrides to
   * @param overrides - Array of override specifications
   */
  private applySymbolOverrides(node: SceneNode, overrides: SymbolOverride[]): void {
    if (!overrides || overrides.length === 0) return

    for (const override of overrides) {
      const { guidPath } = override

      // Find the target node by following the GUID path
      let targetNode: SceneNode | undefined = node
      for (const guidRef of guidPath.guids) {
        if (!targetNode?.children) {
          targetNode = undefined
          break
        }
        // Convert GuidRef to string format to match node.guid
        const targetGuid = `${guidRef.sessionID}:${guidRef.localID}`
        targetNode = targetNode.children.find((child) => child.guid === targetGuid)
      }

      if (!targetNode) {
        console.warn('Override target not found:', guidPath)
        continue
      }

      // Apply the overrides to target node properties
      const props = targetNode.properties as any

      // Apply fill paints
      if (override.fillPaints) {
        props.fillPaints = override.fillPaints
      }

      // Apply stroke paints
      if (override.strokePaints) {
        props.strokePaints = override.strokePaints
      }

      // Apply stroke weight
      if (override.strokeWeight !== undefined) {
        props.strokeWeight = override.strokeWeight
      }

      // Apply size
      if (override.size) {
        props.size = override.size
      }

      // Apply variable consumption map
      if (override.variableConsumptionMap) {
        props.variableConsumptionMap = override.variableConsumptionMap
      }

      // Apply parameter consumption map
      if (override.parameterConsumptionMap) {
        props.parameterConsumptionMap = override.parameterConsumptionMap
      }
    }
  }

  /**
   * Recursively filter out hidden nodes
   *
   * Removes nodes with visible: false from the tree after instance expansion.
   * This is necessary because symbol definitions may contain hidden nodes that
   * should not appear in the final component tree.
   *
   * @param node - Node to check and filter
   * @returns The node if visible, or null if hidden
   */
  private filterHiddenNodes(node: SceneNode): SceneNode | null {
    // Check if node is hidden
    const props = node.properties as any
    if (props?.visible === false) {
      return null
    }

    // Filter children recursively
    if (node.children) {
      node.children = node.children
        .map((child) => this.filterHiddenNodes(child))
        .filter((child): child is SceneNode => child !== null)
    }

    return node
  }

  /**
   * Get the "Internal Only Canvas" which contains symbol definitions
   */
  private getInternalOnlyCanvas(scene: FigmaInternalScene): SceneNode | null {
    // The Internal Only Canvas is typically the second canvas child
    // It's marked as hidden (visible: false)
    const canvases = scene.root.children || []

    for (const canvas of canvases) {
      if (canvas.name === 'Internal Only Canvas') {
        return canvas
      }

      // Also check by visibility flag
      const props = canvas.properties as any
      if (props?.visible === false) {
        return canvas
      }
    }

    return canvases[1] || null
  }

  /**
   * Clone a node (deep copy)
   */
  private cloneNode(node: SceneNode): SceneNode {
    return JSON.parse(JSON.stringify(node)) as SceneNode
  }

  /**
   * Transform SceneNode to ComponentNode
   *
   * @param node - Source scene node
   * @param originalNode - Original node reference (for sorting children by position)
   * @param isRoot - Whether this is the root/top-level node (for sizing intent detection)
   * @returns Transformed component node
   */
  private transformNode(node: SceneNode, originalNode?: SceneNode, isRoot = false): ComponentNode {
    const props = node.properties as any
    const nodeRef = originalNode || node

    // Extract layout, style, content, metadata (pass isRoot for sizing intent detection)
    let layout = extractLayout(props, isRoot, this.options.includeLayoutDimensions)

    // Remove position coordinates for auto-layout children (unless includePositionCoords is true)
    // Position coords are Figma-internal and don't translate to CSS flexbox
    if (!this.options.includePositionCoords && layout?.position) {
      // Only keep position if the node is absolutely positioned
      // For auto-layout children, position is determined by parent flex properties
      const isAbsolutePositioned = props.position === 'ABSOLUTE'
      if (!isAbsolutePositioned) {
        const { position, ...layoutWithoutPosition } = layout
        layout = Object.keys(layoutWithoutPosition).length > 0 ? (layoutWithoutPosition as any) : undefined
      }
    }

    // Use flexible dimensions for containers when enabled (prevents viewport overflow)
    if (this.options.useFlexibleDimensions && layout) {
      const isContainer = node.children && node.children.length > 0
      if (isContainer && layout.mode && layout.mode !== 'none') {
        // For auto-layout containers, remove width if it has fixed sizing intent
        // (Note: with new format, width is DimensionValue with sizingIntent)
        if (layout.width && layout.width.sizingIntent === 'fixed') {
          delete (layout as any).width
        }
        // Keep height as min-height suggestion (handled by implementer)
      }
    }

    const content = this.extractContent(node)
    const meta = this.options.includeMetadata ? this.extractMetadata(node) : undefined

    // Omit style for structural frames, TEXT nodes, or when includeNodeStyle is false
    // TEXT nodes: all styling is in content.text.style.color, background would be confusing
    // Structural frames: user preference to remove styling completely
    let style = undefined
    if (!meta?.structural && node.type !== 'TEXT' && this.options.includeNodeStyle) {
      style = extractStyle(props)
    }

    // Transform children recursively (children are never root)
    let children = node.children?.map((child, index) => {
      const originalChild = nodeRef.children?.[index]
      return this.transformNode(child, originalChild, false)
    })

    // Sort children by position based on parent's layout mode (CRITICAL FIX for rendering order)
    if (children && this.options.sortChildrenByPosition && nodeRef.children) {
      const childrenWithPos = children.map((child, index) => {
        const originalChild = nodeRef.children?.[index]
        const transform = (originalChild?.properties as any)?.transform
        // Transform object format: {m00, m01, m02, m10, m11, m12}
        // m02 is the x-position (tx), m12 is the y-position (ty)

        // Use appropriate axis based on parent's layout mode
        let sortPos: number
        if (props.stackMode === 'HORIZONTAL') {
          // For horizontal layouts, sort left-to-right by X position
          sortPos = transform?.m02 ?? 0
        } else {
          // For vertical layouts (or no auto-layout), sort top-to-bottom by Y position
          sortPos = transform?.m12 ?? 0
        }

        return { child, sortPos }
      })

      // Sort by the determined position (X for horizontal, Y for vertical)
      childrenWithPos.sort((a, b) => a.sortPos - b.sortPos)
      children = childrenWithPos.map((item) => item.child)
    }

    // Flatten structural single-child wrappers if enabled (reduces unnecessary nesting)
    if (this.options.flattenStructuralWrappers && meta?.structural && children?.length === 1) {
      const child = children[0]
      if (child) {
        // Merge layout properties from wrapper into child
        if (layout && child.layout) {
          child.layout = { ...layout, ...child.layout }
        } else if (layout) {
          child.layout = layout
        }
        return child
      }
    }

    const componentNode: ComponentNode = {
      id: node.guid, // Always include ID for API operations (e.g., image downloading)
      type: this.mapNodeType(node.type),
      name: node.name,
    }

    // Collect hints for this node only (not including children)
    const nodeHints = collectNodeHints(node, this.assetLookupMap)
    if (nodeHints.length > 0) {
      componentNode.hints = nodeHints
    }

    if (layout) componentNode.layout = layout
    if (style) componentNode.style = style
    if (content) componentNode.content = content
    if (meta) componentNode.meta = meta
    if (children && children.length > 0) componentNode.children = children

    return componentNode
  }

  /**
   * Map Figma node type to component node type
   */
  private mapNodeType(figmaType: string): ComponentNodeType {
    switch (figmaType) {
      case 'FRAME':
        return 'Frame'
      case 'TEXT':
        return 'Text'
      case 'INSTANCE':
        return 'Instance'
      case 'RECTANGLE':
        return 'Rectangle'
      case 'GROUP':
        return 'Group'
      case 'SYMBOL':
      case 'COMPONENT':
        return 'Component'
      case 'VECTOR':
        return 'Vector'
      case 'ELLIPSE':
        return 'Ellipse'
      case 'LINE':
        return 'Line'
      case 'REGULAR_POLYGON':
        return 'RegularPolygon'
      case 'STAR':
        return 'Star'
      default:
        return 'Frame' // Default fallback
    }
  }

  /**
   * Parse font weight from font style string
   * E.g., "Regular" -> 400, "Bold" -> 700, "Medium" -> 500
   */
  private parseFontWeight(fontStyle?: string): number | undefined {
    if (!fontStyle) return undefined

    const style = fontStyle.toLowerCase()

    // Direct weight mappings
    if (style.includes('thin')) return 100
    if (style.includes('extralight') || style.includes('ultra light')) return 200
    if (style.includes('light')) return 300
    if (style.includes('regular') || style.includes('normal')) return 400
    if (style.includes('medium')) return 500
    if (style.includes('semibold') || style.includes('demi')) return 600
    if (style.includes('bold') && !style.includes('extrabold')) return 700
    if (style.includes('extrabold') || style.includes('ultra bold')) return 800
    if (style.includes('black') || style.includes('heavy')) return 900

    // Default to regular
    return 400
  }

  /**
   * Extract content properties from node
   */
  private extractContent(node: SceneNode): ContentProperties | undefined {
    if (node.type === 'TEXT') {
      const props = node.properties as TextProps

      // Extract text content
      const text = props.textData?.characters

      if (!text) {
        return undefined
      }

      const textStyle: import('./types/component-tree.js').TextStyle = {}
      if (props.fontName?.family) textStyle.fontFamily = props.fontName.family
      if (props.fontSize) textStyle.fontSize = props.fontSize
      const fontWeight = this.parseFontWeight(props.fontName?.style)
      if (fontWeight) textStyle.fontWeight = fontWeight
      const color = resolveSingleColor(props.fillPaints)
      if (color) textStyle.color = color

      const textContent: import('./types/component-tree.js').TextContent = {
        value: text,
      }
      if (Object.keys(textStyle).length > 0 && this.options.includeTextStyle) {
        textContent.style = textStyle
      }

      const content: ContentProperties = {
        text: textContent,
      }

      return content
    }

    // TODO: Handle component properties for instances
    return undefined
  }

  /**
   * Extract metadata properties from node
   */
  private extractMetadata(node: SceneNode): MetadataProperties | undefined {
    const role = detectComponentRole(node)
    const htmlTag = suggestHtmlTag(node, role)

    // Detect structural frames and icons
    const iconType = this.options.detectIcons ? detectIconType(node) : undefined
    const structural = this.options.detectStructuralFrames ? isStructuralFrame(node) : false

    const meta: MetadataProperties = {
      sourceId: node.guid,
      htmlTag,
    }

    if (role) meta.role = role

    // Extract instance expansion metadata
    const instanceMetadata = (node.properties as any).__instanceMetadata
    if (instanceMetadata) {
      meta.expandedFromInstance = true
      meta.componentName = instanceMetadata.componentName
      meta.componentId = instanceMetadata.componentId
    }

    // Add structural flag
    if (structural) {
      meta.structural = true
    }

    // Add icon metadata
    if (iconType) {
      meta.iconType = iconType

      // Extract simple description by removing icon prefix
      const description = iconType.replace(/^icon\s*[\/\-_]\s*/i, '').trim()
      if (description && description !== iconType) {
        meta.iconDescription = description
      }
    }

    return meta
  }
}

/**
 * Convenience function to transform clipboard data
 *
 * @param clipboardData - Raw clipboard data
 * @param options - Transformation options
 * @returns Transformed component tree
 */
export function transformClipboard(
  clipboardData: unknown,
  options?: TransformOptions,
): ComponentTree {
  const transformer = new ComponentTreeTransformer()
  return transformer.transform(clipboardData, options)
}
